import React, { createContext, useState, useContext, useEffect } from 'react';
import { translateApiMessage } from '../utils/apiMessages';
import { mayBeSignedIn } from '../utils/signedInHint';

const AuthContext = createContext(null);

/**
 * The session as read by something that can do without one.
 *
 * `useAuth` throws outside a provider, which is right for a screen that needs
 * the account — a missing provider there is a wiring mistake, not a visitor.
 * The city in view is asked for by pieces that also render on their own, so
 * they read it this way: no provider reads as "nobody is signed in".
 */
export const useOptionalAuth = () => useContext(AuthContext) ?? { user: null, isAuthenticated: false };

// Relative, as everywhere: Caddy serves the apps and the API on one origin, in
// development as in production. The absolute localhost:8000 this used to fall
// back to predates that, and would bypass the proxy — and its cookie — entirely.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const ROLE_USER = 'ROLE_USER';
export const ROLE_PRO = 'ROLE_PRO';
export const ROLE_ADMIN = 'ROLE_ADMIN';
// Held while the account moderates at least one city; which ones is the
// moderation page's to ask.
export const ROLE_MODERATOR = 'ROLE_MODERATOR';

// A provider is also a resident, and an admin is both. Mirrors the backend
// role_hierarchy so the UI never has to special-case an admin.
/*
 * There is no inheritance table any more. The API returns the whole set the
 * account holds — ROLE_USER on everybody, plus ROLE_PRO and ROLE_ADMIN
 * when they apply — so membership is a plain lookup. The table this replaced
 * claimed an admin was also a professional, which was never true.
 */

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if session is valid by fetching current user
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    // No hint, no session: asking would only earn a 401.
    if (!mayBeSignedIn()) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'GET',
        credentials: 'include', // Include cookies for session
        headers: JSON_HEADERS,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.data);
        }
      } else if (response.status === 401) {
        // Session expired or no session
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include', // Include cookies for session
        headers: JSON_HEADERS,
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.data);
          return { success: true };
        }
        return { success: false, error: translateApiMessage(data.error) || 'Connexion impossible' };
      }

      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || 'Identifiants incorrects',
        // "email_not_confirmed" is the one refusal the form can act on.
        code: errorData.code,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Connexion impossible',
      };
    }
  };

  /**
   * Sign up. No session is opened: the account cannot sign in until the
   * address is confirmed through the link just emailed, so the form says so
   * and waits.
   *
   * One endpoint for everybody. `professional` is a product question — "do
   * you also offer a service" — and the backend turns it into ROLE_PRO.
   * The payload never names a role: a public endpoint that could would be the
   * privilege-escalation hole all over again.
   *
   * Field-level errors from the API are returned in `violations`, keyed by
   * field name, so the form can show each message next to its input.
   */
  const register = async ({
    username,
    email,
    password,
    professional = false,
    acceptTerms = false,
    citySlug = null,
    neighborhoodUuid = null,
  }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: JSON_HEADERS,
        // Left out when empty: with one city open the API attaches the
        // account to it, and a neighbourhood is optional.
        body: JSON.stringify({
          username,
          email,
          password,
          professional,
          acceptTerms,
          ...(citySlug ? { citySlug } : {}),
          ...(neighborhoodUuid ? { neighborhoodUuid } : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return {
          success: false,
          error: translateApiMessage(data.error) || 'Inscription impossible',
          violations: data.violations || {},
        };
      }

      return { success: true, user: data.data };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Inscription impossible',
        violations: {},
      };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Include cookies for session
        headers: JSON_HEADERS,
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      setUser(null);
    }
  };

  /**
   * The letter in the avatar, taken from the handle every account has.
   *
   * One letter, not two: a handle is a name, not a firstname and a surname, so
   * the first two characters cut it mid-word — "David M." showed up as "DA".
   * Leading punctuation and spaces are skipped so the letter is one somebody
   * recognises.
   */
  const getInitials = () => {
    if (!user) return '';

    const handle = String(user.username || user.email || '');
    const firstLetter = handle.match(/\p{L}|\p{N}/u);

    return (firstLetter?.[0] ?? '').toUpperCase();
  };

  /**
   * Name to show next to the avatar and on published content.
   */
  const getDisplayName = () => {
    if (!user) return '';

    return user.username || user.email || '';
  };

  /**
   * Short, friendly form for greetings. The same handle as getDisplayName()
   * today, kept separate because the two answer different questions and only
   * one of them would follow a real display name if accounts ever get one.
   */
  const getShortName = () => {
    if (!user) return '';

    return user.username || user.email || '';
  };

  const hasRole = (role) => Array.isArray(user?.roles) && user.roles.includes(role);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        // Re-read the account: the professional role is granted by the API
        // when the first listing is written, so the interface has to catch up
        // without a sign-out.
        refreshUser: fetchUserData,
        logout,
        isAuthenticated: !!user,
        getInitials,
        getDisplayName,
        getShortName,
        hasRole,
        isAdmin: hasRole(ROLE_ADMIN),
        isProvider: hasRole(ROLE_PRO),
        isModerator: hasRole(ROLE_MODERATOR),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
